import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import supabaseClient from '@/api/supabaseClient';
import { toast } from 'sonner';
import { Database } from '@/_constants/database.types';
import { PaginationState } from '@tanstack/react-table';

type QuestionRow = Database['public']['Tables']['questions']['Row'];
type CategoryCode = Database['public']['Enums']['category_code'];
type QuestionType = Database['public']['Enums']['question_types'];
type ContentStatus = Database['public']['Enums']['content_status'];

// Derive the category enum value from a category_code (e.g. 'LANG-P2-Q7A' → 'Q7A', 'LANG-P2-B' → 'SectionB')
const categoryFromCode = (code: CategoryCode): QuestionType => {
  const suffix = code.split('-').pop() || '';
  return (suffix === 'B' ? 'SectionB' : suffix) as QuestionType;
};

export type AdminPracticeQuestion = Pick<
  QuestionRow,
  | 'id'
  | 'category_code'
  | 'content'
  | 'marks'
  | 'status'
  | 'created_at'
  | 'model_answer'
  | 'mark_scheme_content'
  | 'reading_insert_content'
  | 'reading_insert_content_2'
  | 'reading_insert_page_numbers'
>;

export interface PracticeQuestionFilters {
  categoryCodes?: CategoryCode[]; // resolved from subject/paper in the page
  categoryCode?: CategoryCode;
  status?: ContentStatus;
  search?: string;
}

export interface UseAdminPracticeQuestionsParams {
  pagination: PaginationState;
  filters?: PracticeQuestionFilters;
}

export interface AdminPracticeQuestionsResult {
  data: AdminPracticeQuestion[];
  rowCount: number;
}

// Fetch list of practice questions
const fetchAdminPracticeQuestions = async (
  params: UseAdminPracticeQuestionsParams
): Promise<AdminPracticeQuestionsResult> => {
  let query = supabaseClient
    .from('questions')
    .select(
      'id, category_code, content, marks, status, created_at, model_answer, mark_scheme_content, reading_insert_content, reading_insert_content_2, reading_insert_page_numbers',
      { count: 'exact' }
    )
    // Practice questions: either is_practice_only = true or past_paper_id is null
    .or('is_practice_only.eq.true,past_paper_id.is.null');

  // Apply filters
  const { filters } = params;
  if (filters) {
    if (filters.categoryCode) {
      // Specific category takes priority — ignore subject/paper categoryCodes
      query = query.eq('category_code', filters.categoryCode);
    } else if (filters.categoryCodes && filters.categoryCodes.length > 0) {
      query = query.in('category_code', filters.categoryCodes);
    }
    if (filters.status) {
      query = query.eq('status', filters.status);
    }
    if (filters.search) {
      query = query.ilike('content', `%${filters.search}%`);
    }
  }

  // Default sort by created_at descending (newest first)
  query = query.order('created_at', { ascending: false });

  // Apply pagination
  const { pageIndex, pageSize } = params.pagination;
  const from = pageIndex * pageSize;
  const to = from + pageSize - 1;
  query = query.range(from, to);

  const { data, error, count } = await query;

  if (error) {
    console.error('Error fetching practice questions:', error);
    toast.error('Failed to load practice questions. Please try again.');
    throw error;
  }

  return {
    data: data || [],
    rowCount: count ?? 0,
  };
};

const useAdminPracticeQuestions = (params: UseAdminPracticeQuestionsParams) => {
  return useQuery({
    queryKey: ['admin-practice-questions', params],
    queryFn: () => fetchAdminPracticeQuestions(params),
    placeholderData: keepPreviousData,
  });
};

// Fetch single practice question
const fetchAdminPracticeQuestion = async (id: string): Promise<QuestionRow> => {
  const { data, error } = await supabaseClient.from('questions').select('*').eq('id', id).single();

  if (error) {
    console.error('Error fetching practice question:', error);
    throw error;
  }

  return data;
};

export const useAdminPracticeQuestion = (id: string | undefined) => {
  return useQuery({
    queryKey: ['admin-practice-question', id],
    queryFn: () => fetchAdminPracticeQuestion(id!),
    enabled: !!id,
  });
};

// Create practice question
interface CreatePracticeQuestionParams {
  category_code: CategoryCode;
  content: string;
  marks: number;
}

const createPracticeQuestion = async (params: CreatePracticeQuestionParams): Promise<QuestionRow> => {
  const { data, error } = await supabaseClient
    .from('questions')
    .insert({
      category_code: params.category_code,
      category: categoryFromCode(params.category_code),
      content: params.content,
      marks: params.marks,
      is_practice_only: true,
      status: 'draft',
    })
    .select()
    .single();

  if (error) {
    console.error('Error creating practice question:', error);
    throw error;
  }

  return data;
};

export const useCreatePracticeQuestion = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createPracticeQuestion,
    onSuccess: () => {
      toast.success('Practice question created successfully');
      queryClient.invalidateQueries({ queryKey: ['admin-practice-questions'] });
    },
    onError: () => {
      toast.error('Failed to create practice question. Please try again.');
    },
  });
};

// Update practice question
interface UpdatePracticeQuestionParams {
  questionId: string;
  updates: {
    category_code?: CategoryCode;
    content?: string;
    marks?: number;
    status?: ContentStatus;
    mark_scheme_content?: unknown;
    model_answer?: string;
    hint_content?: string;
    reading_insert_content?: string | null;
    reading_insert_content_2?: string | null;
    reading_insert_page_numbers?: number[] | null;
    has_image?: boolean;
  };
}

const updatePracticeQuestion = async ({ questionId, updates }: UpdatePracticeQuestionParams): Promise<void> => {
  const resolvedUpdates = updates.category_code
    ? { ...updates, category: categoryFromCode(updates.category_code) }
    : updates;
  const { error } = await supabaseClient.from('questions').update(resolvedUpdates).eq('id', questionId);

  if (error) {
    console.error('Error updating practice question:', error);
    throw error;
  }
};

export const useUpdatePracticeQuestion = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updatePracticeQuestion,
    onSuccess: (_, { questionId }) => {
      toast.success('Practice question updated successfully');
      queryClient.invalidateQueries({ queryKey: ['admin-practice-questions'] });
      queryClient.invalidateQueries({ queryKey: ['admin-practice-question', questionId] });
    },
    onError: () => {
      toast.error('Failed to update practice question. Please try again.');
    },
  });
};

// Delete practice question
const deletePracticeQuestion = async (id: string): Promise<void> => {
  const { error } = await supabaseClient.from('questions').delete().eq('id', id);

  if (error) {
    console.error('Error deleting practice question:', error);
    throw error;
  }
};

export const useDeletePracticeQuestion = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deletePracticeQuestion,
    onSuccess: () => {
      toast.success('Practice question deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['admin-practice-questions'] });
    },
    onError: () => {
      toast.error('Failed to delete practice question. Please try again.');
    },
  });
};

export default useAdminPracticeQuestions;
